import { type URLSearchParams } from 'node:url'
import {
  useSearchParams as nextParams,
  useRouter as nextUseRouter,
  type ReadonlyURLSearchParams,
  usePathname as nextUsePathname,
  useParams as nextSlugParams,
} from 'next/navigation'
import { createContext, useContext, useEffect, useReducer, type FC, type PropsWithChildren } from 'react'
import {
  useNavigate as reactUseNavigate,
  useSearchParams as reactUseSearchParams,
  useParams as reactUseParams,
  type NavigateFunction as ReactNavigateFunction,
  useLocation as reactUseLocation,
  type Params,
} from 'react-router'

/**
 * Navigate method type
 */
export type Navigate = (_path: string) => void

/**
 * Search Params method type
 */
export type SearchParams = () => URLSearchParams | ReadonlyURLSearchParams | { get: (_queryParam: string) => void }

export type SlugParamsType = () => Readonly<Params> | Params
/**
 * Router framework. For react-router use REACT, for next/navigation use NEXT
 */
export enum RouterFramework {
  React = 'react',
  Next = 'next',
}

/**
 * The router state property been injected to a component using useRouter hook
 */
export interface RouterState {
  framework?: RouterFramework
  navigate: Navigate
  searchParams: SearchParams
  slugParams: SlugParamsType
  isCurrentRoute: (_path: string) => boolean
  getCurrentRoute: () => string
}

const initialState: RouterState = {
  navigate: (path: string) => {
    // eslint-disable-next-line no-console -- no provider initialized warning
    console.warn('No Router provider found. Navigate to: ', path)
  },
  searchParams: () => ({ get: (_queryParam: string) => null }),
  isCurrentRoute: (_path: string) => false,
  slugParams: () => ({}) as Readonly<Params>,
  getCurrentRoute: () => '',
}

const RouterContext = createContext(initialState)

enum DispatchTypes {
  SetFramework,
}

interface DispatchProps {
  type: DispatchTypes
  payload: unknown
}

const reducer = (state: RouterState, { type, payload }: DispatchProps): RouterState => {
  switch (type) {
    case DispatchTypes.SetFramework:
      return {
        ...state,
        framework: payload as RouterFramework,
      }
    default:
      return state
  }
}

interface RouterProviderProps extends PropsWithChildren {
  framework: RouterFramework
}

export const RouterProvider: FC<RouterProviderProps> = ({ framework, children }) => {
  const [routerState, dispatch] = useReducer(reducer, initialState)

  let reactNav: ReactNavigateFunction
  let reactSearchParams: URLSearchParams
  let pathname: string | undefined
  let reactSlugParams: () => Readonly<Params>
  try {
    reactNav = reactUseNavigate()
    const [searchParams, _setSearchParams] = reactUseSearchParams()
    const { pathname: reactPathname } = reactUseLocation()
    const slugParams = reactUseParams
    pathname = reactPathname
    reactSearchParams = searchParams
    reactSlugParams = slugParams
  } catch (err) {
    // console.info('Not react-router router')
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AppRouterInstance is not exported from next/navigation
  let nextRouter: any
  let nextSearchParams: ReadonlyURLSearchParams
  let nextSluggedParams: () => Params
  try {
    nextRouter = nextUseRouter()
    nextSearchParams = nextParams()
    nextSluggedParams = nextSlugParams
    if (!pathname) {
      pathname = nextUsePathname()
    }
  } catch (err) {
    // console.info('Not a next router')
  }

  useEffect(() => {
    dispatch({ type: DispatchTypes.SetFramework, payload: framework })
  }, [framework])

  /**
   * Navigate to a path
   * @param path - Path to navigate
   * @example
   * ``` typescript
   * import { useRouter } from '@egw/router'
   * const { navigate } = useRouter()
   * navigate('/path')
   * ```
   * @throws Error if router framework was not defined
   */
  const navigate: Navigate = (path) => {
    if (!routerState.framework) {
      throw new Error('Router framework was not defined. Use init method to initialize the router')
    }
    if (routerState.framework === RouterFramework.React) {
      reactNav(path)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- AppRouterInstance is not exported from next/navigation
      nextRouter.push(path)
    }
  }

  /**
   * Search Params
   * @example
   * ``` typescript
   * import { useRouter } from '@egw/router'
   * const { searchParams } = useRouter()
   * searchParams().get('alias')
   * ```
   */
  const searchParams: SearchParams = () => {
    if (!routerState.framework) {
      return initialState.searchParams()
    }
    if (routerState.framework === RouterFramework.React) {
      return reactSearchParams
    }
    return nextSearchParams
  }

  /**
   * Slug Params
   * @example
   * ``` typescript
   * import { useRouter } from '@egw/router'
   * const { slugParams } = useRouter()
   * const params = slugParams()
   * const alias = params.alias
   * ```
   */
  const slugParams = () => {
    if (!routerState.framework) {
      return initialState.slugParams()
    }
    if (routerState.framework === RouterFramework.React) {
      return reactSlugParams()
    }
    return nextSluggedParams()
  }

  return (
    <RouterContext.Provider value={{ ...routerState, navigate, searchParams, slugParams }}>
      {children}
    </RouterContext.Provider>
  )
}

// Custom hook to access the router
export const useRouter = (): RouterState => useContext(RouterContext)
